import { getClientId } from 'util/client-id';
import {
  generateInvalidates,
  generateProvides,
  generateProvidesList,
} from 'util/generateTags';
import { getMerchantId } from 'util/merchant-id';
import { incApi } from '../inc-api';
import {
  DIGroupOption,
  DIItemOption,
  DisplayInfoGroup,
  DisplayInfoItem,
  ListGroupResult,
  ListGroupsByDisplayInfoType,
  ListItemResult,
  ListItemsByDisplayInfoType,
  ListMenuResult,
  OrderDetails,
  StoreOrderingForm,
  SalesReportReturnType,
  CreateMenuPayload,
  TransformMenuResult,
  PublishMenuReturnType,
  PublishMenuPayload,
  ListMenusArgsType,
  PostMenusPayloadType,
  TransformGroupResult,
  GroupsPayloadType,
  CreateGroupPayload,
  ItemsPayloadType,
  TransformItemResult,
  CreateItemPayload,
  CreateMerchantPayload,
} from './ordering-service.types';
import {
  transformGetMenuResponse,
  transformMenusPayload,
  transformGetGroupResponse,
  transformItemResponse,
  transformOrderingRequest,
  transformOrderingResponse,
} from './ordering-service.utils';
import { DateTime } from 'luxon';
import { getAdminId } from 'util/user';
import { TagDescription } from '@reduxjs/toolkit/dist/query/endpointDefinitions';

const endpoints = incApi.injectEndpoints({
  overrideExisting: true,
  endpoints: build => ({
    getStoreOrderingSetting: build.query<StoreOrderingForm, string>({
      query: locationId => {
        return {
          url: `incentivio-ordering-service/locations/${locationId}`,
          params: {
            includeChildElements: true,
            includeRelatedStores: true,
          },
          method: 'GET',
        };
      },
      providesTags: generateProvides(
        result => result.locationId,
        'StoreOrderingSetting',
      ),
      transformResponse: res => transformOrderingResponse(res),
    }),

    updateStoreOrderingSetting: build.mutation<
      null,
      { locationId: string; body: StoreOrderingForm }
    >({
      query: ({ locationId, body }) => {
        return {
          url: `incentivio-ordering-service/locations/${locationId}`,
          method: 'PUT',
          body: transformOrderingRequest(body),
          headers: {
            'Inc-Location-Id': body.locationId,
          },
        };
      },
      invalidatesTags: generateInvalidates(
        body => body.locationId,
        'StoreOrderingSetting',
      ),
    }),

    createStoreOrderingSetting: build.mutation<
      { locationId: string },
      StoreOrderingForm
    >({
      query: body => {
        return {
          url: `incentivio-ordering-service/locations`,
          method: 'POST',
          body: transformOrderingRequest({
            ...body,
            clientId: getClientId(),
            locationName: body.title,
            merchantId: getMerchantId(),
          }),
        };
      },
    }),

    deleteStoreOrderingSetting: build.mutation<string, string>({
      query: locationId => {
        return {
          url: `incentivio-ordering-service/locations/${locationId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: () => ['Stores'],
    }),

    sendOrderTransactionReceipt: build.query<
      void,
      { id: string; extTransactionId?: string; fulfilledNotification: string }
    >({
      query: ({ id, extTransactionId }) => ({
        url: `incentivio-ordering-service/orders/${id}/sendreceipt`,
        params: { extTransactionId },
      }),
      keepUnusedDataFor: 0,
    }),

    listItems: build.query<
      ListItemResult,
      { title?: string; count: number; page: number }
    >({
      query: params => ({
        url: `incentivio-ordering-service/adminitems`,
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.items,
        element => element.itemId,
        'Item',
      ),
    }),

    postItems: build.query<ListItemResult, ItemsPayloadType>({
      query: ({ inventory, ...rest }) => ({
        url: `incentivio-ordering-service/items/list`,
        method: 'POST',
        body: {
          ...rest,
          count: +rest.count,
          page: +rest.page,
          imported: rest.imported === 'true',
          includeStores: rest.includeStores === 'true',
          catalogIds: !!rest?.catalogIds
            ? rest?.catalogIds === 'ALL'
              ? []
              : rest?.catalogIds?.split(',').map(id => id)
            : [],
          groupIds: !!rest?.groupIds
            ? rest?.groupIds === 'ALL'
              ? []
              : rest?.groupIds?.split(',').map(id => id)
            : [],
          outOfStock: !!inventory
            ? inventory === 'OUT_OF_STOCK' || inventory === 'OUT_OF_STOCK_TODAY'
            : undefined,
          outOfStockToday: !!inventory
            ? inventory === 'OUT_OF_STOCK_TODAY'
            : undefined,
          incentivioIds: rest?.incentivioIds?.split(','),
          externalIds: rest?.externalIds?.split(','),
        },
      }),
      providesTags: generateProvidesList(
        result => result?.items,
        element => element.itemId,
        'Item',
      ),
    }),

    listGroups: build.query<
      ListGroupResult,
      {
        title?: string;
        count: number;
        page: number;
        includeStores?: boolean;
        isImported?: boolean;
      }
    >({
      query: params => ({
        url: `incentivio-ordering-service/groups`,
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.groups,
        element => element.groupId,
        'Group',
      ),
    }),

    postGroups: build.query<ListGroupResult, GroupsPayloadType>({
      query: body => ({
        url: `incentivio-ordering-service/groups/list`,
        method: 'POST',
        body: {
          ...body,
          count: +body.count,
          page: +body.page,
          imported: body.imported === 'true',
          includeStores: body.includeStores === 'true',
          incentivioIds: body?.incentivioIds?.split(','),
          externalIds: body?.externalIds?.split(','),
          catalogIds: body?.catalogIds?.split(','),
        },
      }),
      providesTags: generateProvidesList(
        result => result?.groups,
        element => element.groupId,
        'Group',
      ),
    }),

    listMenus: build.query<ListMenuResult, ListMenusArgsType>({
      query: params => ({
        url: `incentivio-ordering-service/admincatalogs`,
        params: {
          ...params,
          clientId: getClientId(),
        },
      }),
      providesTags: generateProvidesList(
        result => result?.catalogs,
        element => element.catalogId,
        'Menu',
      ),
    }),

    postMenus: build.query<ListMenuResult, PostMenusPayloadType>({
      query: body => ({
        url: 'incentivio-ordering-service/catalogs/list',
        method: 'POST',
        body: {
          ...body,
          count: +body.count,
          page: +body.page,
          imported: body.imported === 'true',
          includeStores: body.includeStores === 'true',
          storeIds: body?.storeIds
            ?.split(',')
            .map(storeId => (storeId === 'ALL' ? '*' : storeId)),
          incentivioIds: body?.incentivioIds?.split(','),
          externalIds: body?.externalIds?.split(','),
        },
      }),
      providesTags: generateProvidesList(
        result => result?.catalogs,
        element => element.catalogId,
        'Menu',
      ),
    }),

    listAllCatalogMenus: build.query<
      ListMenuResult,
      {
        incentlocationid: string;
        extlocationid: string;
        connectorgatewayname: string;
      }
    >({
      query: params => ({
        url: `view/externallocation/catalogs`,
        params: {
          ...params,
          clientid: getClientId(),
        },
      }),
      transformResponse: responseData => {
        return {
          paging: responseData.paging,
          catalogs: responseData.catalogs.map(item => ({
            id: item.catalogId,
            label: item.title,
          })),
        };
      },
    }),

    publishMenu: build.mutation<PublishMenuReturnType, PublishMenuPayload>({
      query: body => ({
        url: 'incentivio-ordering-service/cachecatalogs',
        method: 'POST',
        body: {
          localDateTime: DateTime.fromISO(DateTime.now().toString(), {
            zone: 'utc',
          }).toFormat("yyyy-MM-dd'T'HH:mm:ss"),
          userId: getAdminId(),
          clientId: getClientId(),
          ...body,
        },
      }),
    }),

    deleteMenu: build.mutation<null, { catalogId: string }>({
      query: ({ catalogId }) => ({
        url: `incentivio-ordering-service/catalogs/${catalogId}`,
        method: 'DELETE',
      }),
      invalidatesTags: generateInvalidates(body => body.catalogId, 'Menu'),
    }),

    deleteMenus: build.mutation<null, { catalogIds: string[] }>({
      query: ({ catalogIds }) => ({
        url: `incentivio-ordering-service/catalogs/delete`,
        method: 'POST',
        body: [...catalogIds],
      }),
      invalidatesTags: (_r, _e, body) =>
        (
          body.catalogIds.map(id => ({ type: 'Menu', id })) as TagDescription<
            'Menu' | 'Menus'
          >[]
        ).concat(['Menus']),
    }),

    getMenu: build.query<TransformMenuResult, { catalogId: string }>({
      query: ({ catalogId }) => ({
        url: `incentivio-ordering-service/admincatalogs/${catalogId}?includeStores=true`,
        method: 'GET',
      }),
      providesTags: generateProvides(result => result.catalogId, 'Menu'),
      transformResponse: transformGetMenuResponse,
    }),

    createMenu: build.mutation<{ catalogId: string }, CreateMenuPayload>({
      query: body => {
        return {
          url: `incentivio-ordering-service/catalogs`,
          method: 'PUT',
          body: transformMenusPayload(body),
        };
      },
      invalidatesTags: generateInvalidates(body => body.catalogId, 'Menu'),
    }),

    getSalesReport: build.query<SalesReportReturnType, any>({
      query: params => {
        return {
          url: `incentivio-ordering-service/dailyadminorders`,
          params: { ...params, merchantId: getMerchantId() },
        };
      },
      providesTags: generateProvidesList(
        result => result?.orders,
        element => element.orderId,
        'SalesOrder',
      ),
    }),

    exportSalesReport: build.mutation({
      query: body => {
        let storeIdsParam = body.storeIds.includes('ALL')
          ? ['*']
          : body.storeIds;
        body = { ...body, storeIds: storeIdsParam };
        return {
          url: 'incentivio-ordering-service/daily-sales-export',
          method: 'POST',
          body,
        };
      },
    }),
    listItemsByDisplayInfo: build.query<
      ListItemsByDisplayInfoType<DIItemOption>,
      { displayInfoTitle?: string; count: number; page: number }
    >({
      query: params => ({
        url: `incentivio-ordering-service/adminitems/by-displayinfo-title`,
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.items,
        element => element.id,
        'DIItem',
      ),
      transformResponse: (
        responseData: ListItemsByDisplayInfoType<DisplayInfoItem>,
      ) => {
        return {
          paging: responseData.paging,
          items: responseData.items.map(item => ({
            responseData: item,
            id: item.id,
            label: item.displayInfoTitles[0],
            category: 'ITEM',
          })),
        };
      },
    }),

    listGroupsByDisplayInfo: build.query<
      ListGroupsByDisplayInfoType<DIGroupOption>,
      { displayInfoTitle?: string; count: number; page: number }
    >({
      query: params => ({
        url: `incentivio-ordering-service/admingroups/by-displayinfo-title`,
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.groups,
        element => element.id,
        'DIGroup',
      ),
      transformResponse: (
        responseData: ListGroupsByDisplayInfoType<DisplayInfoGroup>,
      ) => {
        return {
          paging: responseData.paging,
          groups: responseData.groups.map(group => ({
            responseData: {
              ...group,
              groupType:
                group.groupType === 'OPTION_GROUP' ? 'MODIFIER' : 'MAIN',
            },
            id: group.id,
            label: group.displayInfoTitles[0],
            category: 'GROUP',
          })),
        };
      },
    }),

    getAdminGroup: build.query<TransformGroupResult, { groupId: string }>({
      query: ({ groupId }) => ({
        url: `incentivio-ordering-service/admingroups/${groupId}?includeChildElements=true`,
        method: 'GET',
      }),
      providesTags: generateProvides(result => result.groupId, 'Group'), // [{type: 'Group', id: result.groupId}]
      transformResponse: transformGetGroupResponse,
    }),

    createGroup: build.mutation<{ groupId: string }, CreateGroupPayload>({
      query: body => ({
        url: `incentivio-ordering-service/groups`,
        method: 'PUT',
        body: {
          ...body,
          clientId: getClientId(),
          merchantId: getMerchantId(),
        },
      }),
      invalidatesTags: generateInvalidates(body => body.groupId, 'Group'),
    }),

    deleteGroup: build.mutation<null, { groupId: string }>({
      query: ({ groupId }) => ({
        url: `incentivio-ordering-service/groups/${groupId}`,
        method: 'DELETE',
      }),
      invalidatesTags: generateInvalidates(body => body.groupId, 'Group'),
    }),

    deleteGroups: build.mutation<null, { groupIds: string[] }>({
      query: ({ groupIds }) => ({
        url: `incentivio-ordering-service/groups/delete`,
        method: 'POST',
        body: [...groupIds],
      }),
      invalidatesTags: (_r, _e, body) =>
        (
          body.groupIds.map(id => ({ type: 'Group', id })) as TagDescription<
            'Group' | 'Groups'
          >[]
        ).concat(['Groups']),
    }),

    getOrderDetails: build.query<OrderDetails, any>({
      query: orderId => ({
        url: `incentivio-ordering-service/adminorders/${orderId}`,
        method: 'GET',
      }),
      providesTags: generateProvides(result => result.orderId, 'SalesOrder'),
    }),

    refundOrderPayment: build.mutation({
      query: body => ({
        url: `incentivio-ordering-service/orders/reversepayment`,
        method: 'POST',
        body,
      }),
      invalidatesTags: generateInvalidates(body => body.orderId, 'SalesOrder'),
    }),

    getItem: build.query<TransformItemResult, { itemId: string }>({
      query: ({ itemId }) => ({
        url: `incentivio-ordering-service/adminitems/${itemId}?includeChildElements=true`,
        method: 'GET',
      }),
      providesTags: generateProvides(result => result.itemId, 'Item'),
      transformResponse: transformItemResponse,
    }),

    createItem: build.mutation<{ groupId: string }, CreateItemPayload>({
      query: body => ({
        url: `incentivio-ordering-service/items`,
        method: 'PUT',
        body: {
          ...body,
          clientId: getClientId(),
          merchantId: getMerchantId(),
        },
      }),
      invalidatesTags: generateInvalidates(body => body.itemId, 'Item'),
    }),

    deleteItem: build.mutation<null, { itemId: string }>({
      query: ({ itemId }) => ({
        url: `incentivio-ordering-service/items/${itemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: generateInvalidates(body => body.itemId, 'Item'),
    }),

    deleteItems: build.mutation<null, { itemIds: string[] }>({
      query: ({ itemIds }) => ({
        url: `incentivio-ordering-service/items/delete`,
        method: 'POST',
        body: [...itemIds],
      }),
      invalidatesTags: (_r, _e, body) =>
        (
          body.itemIds.map(id => ({ type: 'Item', id })) as TagDescription<
            'Item' | 'Items'
          >[]
        ).concat(['Items']),
    }),

    itemsBulkUpdate: build.mutation<
      null,
      { ids: string[]; field: string; value: string | object }
    >({
      query: body => ({
        url: `incentivio-ordering-service/items`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, body) =>
        (
          body.ids.map(id => ({ type: 'Item', id })) as TagDescription<
            'Item' | 'Items'
          >[]
        ).concat(['Items']),
    }),

    catalogsBulkUpdate: build.mutation<
      null,
      { ids: string[]; field: string; value: string | object }
    >({
      query: body => ({
        url: `incentivio-ordering-service/catalogs`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, body) =>
        (
          body.ids.map(id => ({ type: 'Menu', id })) as TagDescription<
            'Menu' | 'Menus'
          >[]
        ).concat(['Menus']),
    }),

    groupsBulkUpdate: build.mutation<
      null,
      { ids: string[]; field: string; value: string | object }
    >({
      query: body => ({
        url: `incentivio-ordering-service/groups`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, body) =>
        (
          body.ids.map(id => ({ type: 'Group', id })) as TagDescription<
            'Group' | 'Groups'
          >[]
        ).concat(['Groups']),
    }),

    createMerchantSettings: build.mutation<
      { merchantId: string },
      CreateMerchantPayload
    >({
      query: body => ({
        url: `incentivio-ordering-service/merchants`,
        method: 'POST',
        body: {
          ...body,
          clientId: getClientId(),
        },
      }),
    }),

    getPoolAccountTimezone: build.query<string, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchwithBQ) {
        const response = await fetchwithBQ({
          url: `incentivio-ordering-service/clients/${getClientId()}/poolaccount`,
          responseHandler: response => response.body,
        });
        console.log('resonse', response);
        const { value } = await response.data.getReader().read();

        return { data: new TextDecoder().decode(value, { stream: false }) };
      },
    }),
  }),
});

export const {
  useGetStoreOrderingSettingQuery,
  useUpdateStoreOrderingSettingMutation,
  useCreateStoreOrderingSettingMutation,
  useDeleteStoreOrderingSettingMutation,
  useSendOrderTransactionReceiptQuery,
  useGetSalesReportQuery,
  useListItemsQuery,
  usePostItemsQuery,
  useLazyPostItemsQuery,
  useLazyListItemsQuery,
  useListGroupsQuery,
  usePostGroupsQuery,
  useLazyPostGroupsQuery,
  useLazyListGroupsQuery,
  useListMenusQuery,
  usePostMenusQuery,
  useLazyPostMenusQuery,
  useLazyListMenusQuery,
  usePublishMenuMutation,
  useDeleteMenuMutation,
  useDeleteMenusMutation,
  useGetMenuQuery,
  useCreateMenuMutation,
  useExportSalesReportMutation,
  useLazyListItemsByDisplayInfoQuery,
  useLazyListGroupsByDisplayInfoQuery,
  useGetAdminGroupQuery,
  useCreateGroupMutation,
  useDeleteGroupMutation,
  useDeleteGroupsMutation,
  useGetOrderDetailsQuery,
  useRefundOrderPaymentMutation,
  useListAllCatalogMenusQuery,
  useGetItemQuery,
  useCatalogsBulkUpdateMutation,
  useGroupsBulkUpdateMutation,
  useCreateItemMutation,
  useDeleteItemMutation,
  useDeleteItemsMutation,
  useItemsBulkUpdateMutation,
  useCreateMerchantSettingsMutation,
  useGetPoolAccountTimezoneQuery,
} = endpoints;
